'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, type FC } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { IconsNames } from '@eqi-investimentos/icons/web';
import { Button, Flex, Link, Skeleton, Text } from '@eqi-investimentos/react';
import { PATHS } from '@/config/paths';
import { useGetFinancialPlaningPermissions } from '@/modules/FinancialPlanning/http/client/useGetPermissions';
import { Icon } from '@/components/atoms/Icon';
import { IfRender } from '@/utils/jsx';
import { useGetAllPlannings } from '../../../http/client/getAllPlannings';
import { FinancialPlanningRegister } from '../../Register';
import { FINANCIAL_PLANNING_REGISTER } from '../../Register/RegisterDrawer';
export const FinancialPlanningHomeWidget: FC = () => {
  const {
    t
  } = useTranslation('common');
  const {
    push
  } = useRouter();
  const searchParams = useSearchParams();
  const {
    data: permissions = {
      canAccess: false
    }
  } = useGetFinancialPlaningPermissions();
  const action = searchParams.get('action');
  const {
    data: financialPlan,
    isFetching: isLoadingFp
  } = useGetAllPlannings();
  const shouldShowRegister = !financialPlan;
  const cards = t<Card[] | undefined>('screens.financialPlanning.home.cards', null, {
    returnObjects: true
  });
  useEffect(() => {
    const shouldDispatchAction = !isLoadingFp && action === 'create-financial-plan' && shouldShowRegister;
    if (shouldDispatchAction) {
      window.dispatchEvent(new Event(`open-chakra-modal-${FINANCIAL_PLANNING_REGISTER}`));
    }
  }, [action, shouldShowRegister, isLoadingFp]);
  if (!permissions.canAccess) return <></>;
  if (isLoadingFp) {
    return <Flex direction="column" w="calc(100% - 2rem)">
        <Skeleton w="calc(100% - 2rem)" h="15rem" />
      </Flex>;
  }
  if (shouldShowRegister) {
    return <FinancialPlanningRegister />;
  }
  return <Flex direction="column" gap="l" p="2xl" bgColor="background.brandLight" borderRadius="lg" w="calc(100% - 2rem)" maxH="14.875rem" data-sentry-element="Flex" data-sentry-component="FinancialPlanningHomeWidget" data-sentry-source-file="index.tsx">
      <Text fontSize="lg" fontWeight="normal" lineHeight="110%" data-sentry-element="Text" data-sentry-source-file="index.tsx">
        {t('attributes.financialPlanning')}
      </Text>
      <Flex gap={5} alignItems="center" data-sentry-element="Flex" data-sentry-source-file="index.tsx">
        {Array.isArray(cards) && cards.map(({
        icon,
        text,
        href,
        comming_soon
      }) => <Flex key={text} pos="relative" w="13.75rem" h="full" as={Link} href={!!comming_soon ? '#' : href} onClick={e => {
        if (comming_soon) {
          e.stopPropagation();
        }
      }} direction="column" gap="0.75rem" p="1.5rem" borderRadius="0.5rem" cursor={comming_soon ? 'not-allowed' : 'pointer'} bgColor={comming_soon ? 'action.disabled' : 'background.primary'} _hover={{
        bgColor: 'interaction.light',
        textDecoration: 'none'
      }}>
              <IfRender condition={!!comming_soon}>
                <Flex pos="absolute" top="s" right="s" bg="content.brand" px="8" borderRadius="2xs">
                  <Text size="m" fontWeight="semibold" color="content.inverse">
                    {t('attributes.soon')}
                  </Text>
                </Flex>
              </IfRender>
              <Icon iconName={icon} color={comming_soon ? 'content.tertiary' : 'content.brand'} width={24} height={24} />
              <Text fontSize="md" fontWeight="normal" color={comming_soon ? 'content.tertiary' : 'content.primary'}>
                {text}
              </Text>
            </Flex>)}
        <Flex direction="column" gap={5} data-sentry-element="Flex" data-sentry-source-file="index.tsx">
          <Button h="2.75rem" onClick={() => push(PATHS.financialPlanning.index)} data-sentry-element="Button" data-sentry-source-file="index.tsx">
            Resumo Planejamento Financeiro
          </Button>
          <Button h="2.75rem" variant="outline" isDisabled data-sentry-element="Button" data-sentry-source-file="index.tsx">
            Relatório Planejamento Financeiro
          </Button>
        </Flex>
      </Flex>
    </Flex>;
};
export type Card = {
  icon: IconsNames;
  text: string;
  href: string;
  comming_soon?: boolean;
};